import { useMemo, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import EventIcon from '@mui/icons-material/EventNote';

// Contexts and Components
import { PageHeaderCard } from '../../components/common';
import { useAuth } from '../../contexts/AuthContext';

// Services
import { activitiesAPI } from '../../services/api';

// Utils and Constants
import { formatDateTime, getActivityVisualConfig } from '../../utils/helper';

const typeConfig = {
  completed: { label: 'Completed', color: 'success' },
  created: { label: 'Created', color: 'primary' },
  updated: { label: 'Updated', color: 'secondary' },
  warning: { label: 'Warning', color: 'warning' },
};

const toTitleCase = (value) => {
  if (!value || typeof value !== 'string') {
    return '-';
  }

  return value
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const ActivityDetail = () => {
  const { id } = useParams();
  const { getActiveCompany } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const activeCompany = useMemo(() => getActiveCompany(), [getActiveCompany]);
  const activeCompanyId = activeCompany?.id || null;
  const [activity, setActivity] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchActivity = async () => {
      if (!id || !activeCompanyId) {
        setActivity(null);
        return;
      }

      setIsLoading(true);
      setError('');
      try {
        const result = await activitiesAPI.getById(id, activeCompanyId);
        const visuals = getActivityVisualConfig(result.action, result.entityType, result.metadata);
        setActivity({
          ...result,
          icon: visuals.icon,
          color: visuals.color,
          type: visuals.type,
          user: result.actor?.name || 'System',
        });
      } catch (fetchError) {
        setActivity(null);
        setError(fetchError.message || 'Failed to fetch activity detail');
      } finally {
        setIsLoading(false);
      }
    };

    fetchActivity();
  }, [id, activeCompanyId]);

  if (isLoading) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mb: 2 }}
        >
          Back
        </Button>
        <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'grey.200', p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            Loading activity...
          </Typography>
        </Card>
      </Box>
    );
  }

  if (!activity) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mb: 2 }}
        >
          Back
        </Button>
        <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'grey.200', p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color={error ? 'error.main' : 'text.secondary'}>
            {error || 'Activity not found'}
          </Typography>
        </Card>
      </Box>
    );
  }

  const IconComponent = activity.icon;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mb: 2 }}
        >
          Back
        </Button>
        <PageHeaderCard
          mb={0}
          title="Activity Details"
          subtitle="View detailed information about this activity"
          titleVariant={isMobile ? 'h5' : 'h4'}
          titleFontWeight={700}
        />
      </Box>

      {/* Main Content */}
      <Card
        elevation={0}
        sx={{
          borderRadius: 3,
          backgroundColor: 'background.paper',
          boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
          border: '1px solid',
          borderColor: 'grey.100',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
          {/* Activity Header */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                backgroundColor: 'background.paper',
                border: '3px solid',
                borderColor: activity.color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <IconComponent sx={{ color: activity.color, fontSize: 28 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                {activity.title || `${toTitleCase(activity.entityType)} activity`}
              </Typography>
              <Chip
                label={typeConfig[activity.type]?.label || activity.type}
                color={typeConfig[activity.type]?.color || 'default'}
                size={isMobile ? 'small' : 'medium'}
                sx={{ fontWeight: 500 }}
              />
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Activity Details */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
              Description
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
              {activity.message}
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Metadata */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  backgroundColor: 'primary.50',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <PersonIcon sx={{ color: 'primary.main', fontSize: 20 }} />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  User
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {activity.user}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  backgroundColor: 'secondary.50',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <AccessTimeIcon sx={{ color: 'secondary.main', fontSize: 20 }} />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Time
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {formatDateTime(activity.createdAt)}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  backgroundColor: 'success.50',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <EventIcon sx={{ color: 'success.main', fontSize: 20 }} />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Related Project
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {activity.projectName || activity.projectId || '-'}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  backgroundColor: 'warning.50',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <PersonIcon sx={{ color: 'warning.main', fontSize: 20 }} />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Updated Object
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {toTitleCase(activity.entityType)}
                </Typography>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ActivityDetail;
